import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { api } from '@/lib/api-client'
import { useAuth } from '@/features/auth/auth-context'
import { routeRealtimeEvent, type RealtimeEvent } from '@/features/realtime/realtime-events'

const CHANNELS = ['grading', 'tests', 'auth', 'results']
const RECONNECT_BASE_MS = 3_000
const RECONNECT_MAX_MS = 30_000
const RECONNECT_JITTER_MS = 1_000

// IELTS-381/394: the middleware ACCEPTS the socket before closing it on a bad
// token — that is the only way a browser gets to read the 4001/4002 close code —
// so `open` firing does not mean the connection is good. A connection has to
// hold this long before the backoff ladder is allowed back to its bottom rung,
// or a stale token retries at the base delay forever.
const CONNECTION_STABLE_MS = 5_000

/**
 * `MocktestUpdatesConsumer` closes with this ONLY for genuine, terminal
 * revocation: the account is suspended/deactivated, or its `auth_generation`
 * was bumped. No later reconnect can succeed, so it is the one close code this
 * bridge treats as final.
 *
 * It deliberately does NOT cover a merely-superseded token. The server sends
 * `SESSION_STALE_CLOSE_CODE` for that (a refresh advances the account's fence
 * past the socket's fixed token), and this bridge lets that fall through to a
 * normal reconnect, which re-reads the rotated `authToken`. Overloading both
 * onto 4004 stranded every socket open past one ~30-minute refresh.
 */
const ACCESS_REVOKED_CLOSE_CODE = 4004

/**
 * Recoverable counterpart to 4004: the account is fine but this socket's token
 * was superseded (refresh or sign-out). Named for documentation and tests; the
 * bridge needs no special branch for it because every non-4004 code already
 * reconnects with a freshly read token. Exported so the test asserts against
 * the real contract value rather than a magic number.
 */
export const SESSION_STALE_CLOSE_CODE = 4002

// The query families this bridge keeps fresh. Events broadcast while the socket
// was down are gone for good — the server keeps no per-client backlog — so a
// reconnect has to refetch them rather than trust the cache.
const RESYNC_KEYS = [['results'], ['dashboard'], ['tests'], ['notifications']] as const

function websocketUrl(token: string) {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}/ws/mocktest/updates/?token=${encodeURIComponent(token)}`
}

export function RealtimeBridge() {
  const { profile, demo, logout } = useAuth()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!profile || profile.user.role !== 'student' || demo) return

    let socket: WebSocket | null = null
    let reconnectTimer: number | null = null
    let stableTimer: number | null = null
    let attempts = 0
    let stopped = false
    let hasBeenConnected = false

    const clearStableTimer = () => {
      if (stableTimer !== null) {
        window.clearTimeout(stableTimer)
        stableTimer = null
      }
    }

    const scheduleReconnect = () => {
      if (stopped || reconnectTimer !== null) return
      attempts += 1
      const delay =
        Math.min(RECONNECT_BASE_MS * 2 ** (attempts - 1), RECONNECT_MAX_MS) +
        // Jitter: a backend restart drops every session at once, and without it
        // they all come back on the same second.
        Math.random() * RECONNECT_JITTER_MS
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null
        void connect()
      }, delay)
    }

    // Never put the long-lived session token on the wire: the socket URL (with
    // `?token=`) is written verbatim into the access logs, so the credential in
    // it leaks on every handshake. Mint a short-lived, access-scoped ws token
    // instead - a leaked log line then exposes at worst a token that expires in
    // minutes and is refused for anything but this socket.
    const mintWsToken = async (): Promise<string | null> => {
      try {
        const data = await api.get<{ ws_token?: string }>('/auth/ws-token/')
        const wsToken = data?.ws_token
        return typeof wsToken === 'string' && wsToken.length > 0 ? wsToken : null
      } catch {
        return null
      }
    }

    const connect = async () => {
      if (stopped) return
      // Mint per attempt. The token is short-lived and single-purpose, so a
      // reconnect must ask for a fresh one rather than replay a credential the
      // server may already refuse.
      const token = await mintWsToken()
      // The component may have unmounted while the mint was in flight.
      if (stopped) return
      if (!token) {
        // The mint failed, which is not terminal on its own: a transient 5xx,
        // a network blip, or a session that has just ended. Stay on the backoff
        // ladder and try again with a fresh mint. The genuinely-terminal cases
        // do not rely on this branch to stop the loop: a session that has ended
        // is torn down when a non-`/auth/` query rediscovers the 401, announces
        // expiry and nulls `profile`, which unmounts this effect; a revoked
        // account is caught by the 4004 close code below. So rescheduling here
        // cannot spin forever against a dead session.
        scheduleReconnect()
        return
      }
      socket = new WebSocket(websocketUrl(token))
      socket.addEventListener('open', () => {
        socket?.send(JSON.stringify({ type: 'subscribe', channels: CHANNELS }))
        clearStableTimer()
        stableTimer = window.setTimeout(() => {
          stableTimer = null
          attempts = 0
        }, CONNECTION_STABLE_MS)
        // RESYNC: only after a real gap. The first connection of a session has
        // nothing to catch up on — the page has just fetched everything.
        if (hasBeenConnected) {
          for (const queryKey of RESYNC_KEYS) void queryClient.invalidateQueries({ queryKey })
        }
        hasBeenConnected = true
      })
      socket.addEventListener('message', (message) => {
        try {
          const event = JSON.parse(String(message.data)) as RealtimeEvent
          const decision = routeRealtimeEvent(event)
          for (const queryKey of decision.invalidate) void queryClient.invalidateQueries({ queryKey })
          if (decision.notification) window.dispatchEvent(new CustomEvent('ielts-v2-notification', { detail: decision.notification }))
          if (decision.authRevoked) void logout()
        } catch {
          // Ignore malformed events; a single bad payload must not break the live channel.
        }
      })
      socket.addEventListener('close', (event) => {
        socket = null
        clearStableTimer()
        // 4004 is the server saying this account's access was revoked - a
        // terminal verdict it will repeat for every future attempt. Retrying
        // it is the same forever-loop the HTTP layer had. Every other code
        // (including 4001/4002 for an expired token) stays on the backoff
        // ladder, because a refresh rotates `authToken` and the next attempt
        // reads the new one.
        if (event.code === ACCESS_REVOKED_CLOSE_CODE) {
          stopped = true
          return
        }
        scheduleReconnect()
      })
      socket.addEventListener('error', () => socket?.close())
    }

    void connect()
    return () => {
      stopped = true
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer)
      clearStableTimer()
      socket?.close()
    }
  }, [demo, logout, profile, queryClient])

  return null
}
